import React, { useState } from 'react';
import { useCombobox } from 'downshift';
import { ChordType, Note, Chord } from 'tonal';
import { FaCheck, FaTimes } from 'react-icons/fa';

interface ChordAutocompleteProps {
  setSelectedChord: (chord: string) => void;
}

const isLetter = (char: string) => /[a-zA-Z]/.test(char);
const isNumber = (char: string) => /[0-9]/.test(char);

const chordAliases = ChordType.all()
  .flatMap(chord => chord.aliases.filter(alias => /^[a-zA-Z0-9#]*$/.test(alias)));

const splitNoteAndType = (input: string) => {
  const match = input.match(/^([A-Ga-g][#b]*)(.*)$/);
  if (match) {
    return { note: match[1], type: match[2] };
  }
  return { note: '', type: input };
};

const getSuggestions = (inputValue: string) => {
  if (!inputValue) return [];
  const { note, type } = splitNoteAndType(inputValue);
  const typeSuggestions = chordAliases.filter(alias =>
    alias.startsWith(type)
  );

  const sortedSuggestions = typeSuggestions.sort((a, b) => {
    if (a.length !== b.length) {
      return a.length - b.length; // Shortest first
    }
    const aIsLetter = isLetter(a[0]);
    const bIsLetter = isLetter(b[0]);
    if (aIsLetter && !bIsLetter) return -1; // Prioritize letters over numbers
    if (!aIsLetter && bIsLetter) return 1;
    return a.localeCompare(b); // Secondary alphabetical/numeric order
  });

  return sortedSuggestions.map(typeSuggestion => ({
    value: note + typeSuggestion,
    label: note + typeSuggestion
  }));
};

const ChordAutocomplete: React.FC<ChordAutocompleteProps> = ({ setSelectedChord }) => {
  const [inputItems, setInputItems] = useState(getSuggestions(''));

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    selectItem,
    setHighlightedIndex,
    setInputValue,
  } = useCombobox({
    items: inputItems,
    onInputValueChange: ({ inputValue }) => {
      setInputItems(getSuggestions(inputValue || ''));
      if (inputValue) {
        const chord = Chord.get(inputValue);
        if (chord.empty === false) {
          setSelectedChord(inputValue);
        }
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        setSelectedChord(selectedItem.value);
      }
    },
    onHighlightedIndexChange: ({ highlightedIndex }) => {
      if (highlightedIndex !== -1) {
        const item = inputItems[highlightedIndex];
        if (item) {
          setSelectedChord(item.value);
        }
      }
    },
    itemToString: (item) => (item ? item.label : ''),
  });

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Return') {
      const inputValue = (event.currentTarget as HTMLInputElement).value;
      if (inputValue) {
        const suggestions = getSuggestions(inputValue);
        if (suggestions.length > 0) {
          selectItem(suggestions[0]);
        } else {
          setSelectedChord(inputValue);
        }
      }
    }
  };

  const handleClearClick = () => {
    setInputItems([]);
    setSelectedChord('');
    setInputValue(''); // Clear the input field
  };

  return (
    <div className="relative">
      <div className="flex">
        <input
          {...getInputProps({
            placeholder: 'C, Fm, D7sus4, Gdim, etc.',
            onKeyDown: handleKeyDown,
          })}
          className="input input-bordered flex-grow"
        />
        <button onClick={handleClearClick} className="btn btn-square ml-2">
          <FaTimes />
        </button>
      </div>
      <ul
        {...getMenuProps()}
        className="absolute z-10 w-full mt-1 bg-base-100 shadow-lg max-h-60 rounded-md overflow-auto"
      >
        {isOpen &&
          inputItems.map((item, index) => (
            <li
              key={item.value}
              {...getItemProps({ item, index })}
              className={`p-2 cursor-pointer ${highlightedIndex === index ? 'bg-base-200' : ''}`}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {item.label}
            </li>
          ))}
      </ul>
    </div>
  );
};

export default ChordAutocomplete;